import humps from 'humps';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

type QueryObject = Record<string, unknown>;

export function prepareBrandParamsForPagination(
  oldUrl: string | null | undefined,
  brandId: number | string,
): { url: string | null; brandId?: number | string } {
  if (!oldUrl) {
    return { url: null, brandId };
  }
  try {
    const urlObject = new URL(oldUrl);
    const params = new URLSearchParams(urlObject.search);

    // we need to remove url param, otherwise we ll create chain of long urls &url=someurl&url=oldUrl
    // and at some page we'll exceed limit of URL length
    params.delete('url');

    const url = `${urlObject.origin}${urlObject.pathname}?${params.toString()}`;
    // to not duplicate brandId param we need to delete if it exists in newUrl
    return params.has('brandId') ? { url } : { url, brandId };
  } catch {
    return {
      url: oldUrl,
      brandId,
    };
  }
}

function filterNullAndUndefined(objectValue: QueryObject = {}): QueryObject {
  return Object.fromEntries(
    Object.entries(objectValue).filter(([, value]) => value !== null && value !== undefined),
  );
}

/**
 * Takes a url string and converts the query parameters into an object.
 *
 * @param value A url string to parse the query parameters from.
 * @returns An object representation of the query parameters.
 */
export function createQueryObject(value: unknown): Record<string, string> {
  if (isString(value)) {
    const start = value.includes('?') ? value.indexOf('?') : 0;
    const end = value.includes('#') ? value.indexOf('#') : value.length;
    const queryString = value.substring(start, end);
    const urlSearchParams = new URLSearchParams(queryString);
    if (urlSearchParams) {
      return Object.fromEntries(urlSearchParams.entries());
    }
  }
  return {};
}

/**
 * Takes a URL string and creates a camelCase object of the query parameters.
 *
 * @param url String representation of the url.
 * @param queryDefaults Object containing any query parameter defaults you'd like returned if not found in the url.
 * @param excludeCamelCaseQueryObject Will exclude the camelCase query object if true
 * @returns An object of query parameters, where props are converted to camelCase.
 */
export function parseQuery(
  url: string | undefined,
  queryDefaults: QueryObject = {},
  excludeCamelCaseQueryObject = false,
): QueryObject {
  // For now, we are keeping the camelCase and snake_case keys
  const snakeCaseQueryObject = createQueryObject(url);
  const queryObject = {
    ...queryDefaults,
    ...snakeCaseQueryObject,
  };
  if (excludeCamelCaseQueryObject) return queryObject;

  const camelCaseQueryObject = humps.camelizeKeys(snakeCaseQueryObject);
  return {
    ...queryObject,
    ...camelCaseQueryObject,
  };
}

/**
 * Removes null/undefined values and converts property keys to snake_case.
 *
 * @param queryObjects Any camelCase query parameter objects.
 * @returns A snake_case version of the query parameters object with null/undefined values removed.
 */
export function prepareQueryObject(...queryObjects: QueryObject[]): QueryObject {
  return filterNullAndUndefined(
    humps.decamelizeKeys(Object.assign({}, ...queryObjects)) as QueryObject,
  );
}

/**
 * Takes a query object and returns a query string representation.
 *
 * @param query a formatted, or snakeCake query parameter object (pass camelCase object if formatQueryObject is true)
 * @param formatQueryObject decides if prepareQueryObject gets called on query
 * @returns query string representation of the query object.
 */
export function stringifyQuery(query: QueryObject = {}, formatQueryObject = true): string {
  const queryObject = formatQueryObject ? prepareQueryObject(query) : query;
  return Object.entries(queryObject)
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join('&');
}

/**
 * Used by VueRouter.stringifyQuery for converting query objects into strings.
 *
 * @param query camelCase query parameters object.
 * @returns query string to be prepended to url
 */
export function routerStringifyQuery(query: unknown): string {
  if (isObject(query)) {
    const queryString = stringifyQuery(query as QueryObject);
    return queryString || '';
  }
  if (isString(query)) {
    return query;
  }
  return '';
}

export function stringifyObjectValues(query: QueryObject): Record<string, string> {
  return Object.keys(filterNullAndUndefined(query)).reduce<Record<string, string>>(
    (obj, key) => ({
      ...obj,
      [key]: String(query[key]),
    }),
    {},
  );
}

/**
 * Take an existing URL and append some query params, overwriting any conflicting values.
 * @param url URL to modify
 * @param params Keys & values to set as query params
 * @returns The modified URL
 */
export function appendQueryParamsToUrl(url: string, params: QueryObject): string {
  const parsedUrl = new URL(url);
  Object.keys(params).forEach((key) => {
    parsedUrl.searchParams.set(key, params[key] as string);
  });
  return parsedUrl.toString();
}
